import type { OrderStatus } from "@go-novus/admin-api";

import * as m from "../../paraglide/messages.js";
import type { WidgetOrderCard } from "./order-card";

/** 整单状态翻成票头文字；只有取消用出错色（设计稿 2.5 整单状态）。 */
export function orderStatusView(
  status: OrderStatus,
): WidgetOrderCard["status"] {
  switch (status) {
    case "canceled":
      return { label: m.widget_order_status_canceled(), tone: "danger" };
    case "delivered":
      return { label: m.widget_order_status_delivered() };
    case "shipped":
      return { label: m.widget_order_status_shipped() };
    case "partially_shipped":
      return { label: m.widget_order_status_partially_shipped() };
    case "unfulfilled":
      return { label: m.widget_order_status_unfulfilled() };
    case "unknown":
      return { label: m.widget_order_unknown() };
  }
}

const PAYMENT_LABELS: Record<string, () => string> = {
  PAID: m.widget_order_payment_paid,
  PENDING: m.widget_order_payment_pending,
  AUTHORIZED: m.widget_order_payment_authorized,
  PARTIALLY_PAID: m.widget_order_payment_partially_paid,
  REFUNDED: m.widget_order_payment_refunded,
  PARTIALLY_REFUNDED: m.widget_order_payment_partially_refunded,
  VOIDED: m.widget_order_payment_voided,
};

/** Shopify 付款枚举翻成可读文字；不认识的值不显示原文，写 Not provided。 */
export function paymentLabel(status: string | null | undefined): string {
  const label = status ? PAYMENT_LABELS[status.toUpperCase()] : undefined;
  return label ? label() : m.widget_order_unknown();
}

/** 币种的标准小数位（USD 2、JPY 0、KWD 3）；币种无法识别时返回 null。 */
function currencyDigits(currency: string): number | null {
  try {
    return (
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits ?? null
    );
  } catch {
    return null;
  }
}

/**
 * 金额写成「币种 + 按币种小数位补齐的数字」，如 USD 0.00；只补零不四舍五入，
 * 不转成浮点数，大额也保持精确。金额或币种无法识别时保留原文。
 */
export function formatMoney(amount: string, currency: string): string {
  const match = /^(-?\d+)(?:\.(\d+))?$/.exec(amount);
  const whole = match?.[1];
  const digits = currencyDigits(currency);
  if (whole === undefined || digits === null) return `${currency} ${amount}`;
  const fraction = (match?.[2] ?? "").padEnd(digits, "0");
  return `${currency} ${whole}${fraction ? `.${fraction}` : ""}`;
}
